<template>
  <div>
    <div id="google_translate_element"></div>

    <template v-if="isDesktop">
      <div
        id="lang-selector"
        class="dropdown-select-menu notranslate"
        @click.prevent="toggleLang = !toggleLang"
        role="button"
        tabindex="0"
        aria-haspopup="listbox"
        :aria-expanded="toggleLang.toString()"
        @keydown.space.prevent="toggleLang = !toggleLang"
        @keydown.enter.prevent="toggleLang = !toggleLang"
        @keydown.esc.prevent="toggleLang = false"
      >
        <span>{{ lang }}</span>
      </div>
      <transition @enter="enter" @leave="leave" @after-leave="afterLeave">
        <ul
          v-show="toggleLang"
          class="dropdown-select"
          :class="{ 'show-dropdown': toggleLang }"
          role="listbox"
          aria-label="Select language"
        >
          <li class="lang-selection-list">
            <a
              href="#"
              tabindex="0"
              class="lang-selection en notranslate"
              data-lang="en"
              @click.prevent="switchLanguage"
              role="option"
              :aria-selected="lang === 'English'" 
            >
              English
            </a>
          </li>
          <li class="lang-selection-list">
            <a
              href="#"
              tabindex="0"
              class="lang-selection zh-TW notranslate"
              data-lang="zh-TW"
              role="option"
              :aria-selected="lang === 'Chinese (Traditional)'" 
              @click.prevent="switchLanguage"
            >
              Chinese (Traditional)
            </a>
          </li>
          <li class="lang-selection-list">
            <a
              href="#"
              tabindex="0"
              class="lang-selection es notranslate"
              data-lang="es"
              role="option"
              :aria-selected="lang === 'Spanish'" 
              @click.prevent="switchLanguage"
            >
              Spanish
            </a>
          </li>
        </ul>
      </transition>
    </template>

    <template v-else>
      <ul class="language-selection-mobile">
        <li id="lang-selector-mobile" class="mobile menu-item notranslate">
          SELECT LANGUAGE
        </li>
        <li class="lang-selection-list">
          <a
            href="#"
            tabindex="0"
            class="lang-selection mobile menu-item sub en notranslate"
            data-lang="en"
            role="option"
            :aria-selected="lang === 'English'" 
            @click.prevent="switchLanguage"
          >
            English
          </a>
        </li>
        <li class="lang-selection-list">
          <a
            href="#"
            tabindex="0"
            class="lang-selection mobile menu-item sub zh-TW notranslate"
            data-lang="zh-TW"
            role="option"
            :aria-selected="lang === 'Chinese (Traditional)'" 
            @click.prevent="switchLanguage"
          >
            Chinese (Traditional)
          </a>
        </li>
        <li class="lang-selection-list">
          <a
            href="#"
            tabindex="0"
            class="lang-selection mobile menu-item sub es notranslate"
            data-lang="es"
            role="option"
            :aria-selected="lang === 'Spanish'" 
            @click.prevent="switchLanguage"
          >
            Spanish
          </a>
        </li>
      </ul>
    </template>
  </div>
</template>

<script setup>
import { ref, onBeforeMount } from "vue";

const { isDesktop } = useMobileChecker();

const toggleLang = ref(false);
const lang = ref("Select Language");

onBeforeMount(() => {
  // Try to read cookie to set initial lang label
  const googtrans = document.cookie
    .split("; ")
    .find((row) => row.startsWith("googtrans="));
  if (googtrans) {
    const cookieLang = googtrans.split("=")[1].split("/")[2];
    if (cookieLang === "es") lang.value = "Spanish";
    else if (cookieLang === "zh-TW") lang.value = "Chinese (Traditional)";
    else if (cookieLang === "en") lang.value = "English";
    else lang.value = "Select Language";

    lang.value = "Select Language";
  } else {
    document.cookie = "googtrans=/en/en;path=/";
    lang.value = "Select Language";
  }
});

function switchLanguage(event) {
  const selectedLang = event.currentTarget.getAttribute("data-lang");
  if (!selectedLang) return;

  // Update UI immediately
  if (selectedLang === "es") lang.value = "Spanish";
  else if (selectedLang === "zh-TW") lang.value = "Chinese (Traditional)";
  else if (selectedLang === "en") lang.value = "English";
  else lang.value = "Select Language";

  // Set cookie
  document.cookie = `googtrans=/en/${selectedLang};path=/`;

  // Close dropdown
  toggleLang.value = false;

  // Reload the page so Google Translate applies the language on load
  window.location.reload();
}

// Transition functions for dropdown
const enter = (el) => {
  el.style.height = "0";
  el.style.opacity = "0";
  el.style.overflow = "hidden";

  const height = `${el.scrollHeight}px`;

  requestAnimationFrame(() => {
    el.style.transition = "height 0.8s ease, opacity 0.8s ease";
    el.style.height = height;
    el.style.opacity = "1";
  });
};

const leave = (el) => {
  el.style.transition = "none";
  el.style.height = `${el.scrollHeight}px`;
  el.style.opacity = "1";
  el.style.overflow = "hidden";

  // Force reflow
  void el.offsetHeight;

  requestAnimationFrame(() => {
    el.style.transition = "height 0.8s ease-in-out, opacity 0.8s ease-in-out";
    el.style.height = "0";
    el.style.opacity = "0";
  });
};

const afterLeave = (el) => {
  el.style.height = "";
  el.style.opacity = "";
  el.style.overflow = "";
  el.style.transition = "";
};
</script>


<style lang="scss" scoped>
.dropdown-select-menu {
  display: flex;
  justify-content: space-between;
  cursor: pointer;
  overflow: hidden;
  width: 163px;
  line-height: 17px;
  background-color: $black;
  text-transform: uppercase;
  font-size: 10px;
  padding: 5px 0;

  // CARET DOWN
  background: $black url(/Images/logos/caret-down.png) 146px center no-repeat;
  color: $white;
}

.dropdown-select,
.goog-te-combo {
  position: absolute;
  z-index: 9998;
  border-left: 1px solid $black;
  border-right: 1px solid $black;
  border-bottom: 1px solid $black;
  background-color: $white;
  display: none;
  width: 161px;
  max-height: 210px;
  -webkit-box-sizing: content-box;
  -moz-box-sizing: content-box;
  box-sizing: content-box;
  overflow-y: auto;
  overflow-x: hidden;
  list-style-type: none;
  padding-inline-start: 0px;

  &.show-dropdown {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    transition: all 0.3s ease;
  }

  &.mobile {
    position: static;
  }

  li {
    letter-spacing: 1px;
    font-family: $font_poppin;
    padding: 6px 5px;

    &:hover,
    &:hover a {
      background-color: $soft_grey;
    }
  }

  a {
    display: flex;
    text-transform: uppercase;
    text-decoration: none;
    align-items: stretch;
    border: none;
    letter-spacing: 1px;

    img {
      height: 35px;
      width: 45px;
      padding: 5px 10px;
      border: none !important;
      display: block;
    }

    &:hover {
      background-color: $white;
      color: #141e31;
    }
  }
}

@media screen and (max-width: 1024px) {
  .language-selection-mobile {
    padding: 0;
    .menu-item {
      font-family: $font_poppin;
      font-size: 14px;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      color: $black;
      list-style-type: none;

      &.sub {
        padding-left: 14px;

        &::before {
          content: "—";
          font-size: 5px;
          vertical-align: middle;
          padding-right: 5px;
        }
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .dropdown-select-menu {
    width: 265px;
  }
  .dropdown-select {
    right: auto;
    a {
      font-size: 0.9em;
      text-transform: uppercase;
      color: $white;
    }
  }
}
</style>
